import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { isDefinedNotEmpty, Store } from '@softline/core';
import { ModalStore, SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import { SOFTLINE_FEATURE_DELIVERY_NOTE } from '../../delivery-note.shared';
import {
  BeweOptionDialogComponent,
  BottomSheetOption,
} from '../../dialogs/bewe-option-dialog/bewe-option-dialog.component';
import { DefaultCommentDialogComponent } from '../../dialogs/default-comment-dialog/default-comment-dialog.component';
import * as DeliveryNoteStore from '../../stores/delivery-notes.store';
import { DeliveryNotesStore } from '../../stores/index';
import { BeweLf } from '../../types/bewe-lf';
import { SerialNumberViewComponent } from '../serial-number-view/serial-number-view.component';

import { QuantityInputComponent } from '@softline/ui-core';
import { EinheitPipe } from '../../pipes/einheit.pipe';
@Component({
  selector: 'soft-delivery-note-material-card',
  imports: [CommonModule, SerialNumberViewComponent, QuantityInputComponent, EinheitPipe],
  templateUrl: './delivery-note-material-card.component.html',
  styleUrls: ['./delivery-note-material-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class DeliveryNoteMaterialCardComponent {
  private store = inject(Store);

  @Input() idKopfLf = 0;
  @Input() deliveryMaterial: BeweLf | undefined;

  async onChange(quantity: number): Promise<void> {
    if (!this.deliveryMaterial) return;

    await this.store.dispatch(
      SOFTLINE_FEATURE_DELIVERY_NOTE,
      DeliveryNoteStore.actions.preparePatchBewe,
      {
        id: this.idKopfLf,
        bewe: { ...this.deliveryMaterial, menge: quantity },
      }
    );
  }

  async onEditBeweClick(): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<
        { selected?: BottomSheetOption },
        { options: BottomSheetOption[]; itemName?: string }
      >(),
      {
        id: 'DELIVERY_NOTE_RETOUR_BOTTOM_SHEET',
        component: BeweOptionDialogComponent,
        data: {
          options: [{ id: 'ADD_VERMERK', label: 'Vermerk bearbeiten' }],
        },
        dismiss: {
          backdrop: true,
          escape: true,
          button: true,
        },
      }
    );
    if (result === 'DISMISSED') return;

    const vermerkResult = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<
        string | null,
        { vermerk?: string | null; isItem?: boolean }
      >(),
      {
        id: 'DELIVERY_NOTE_RETOUR_COMMENT',
        component: DefaultCommentDialogComponent,
        data: { vermerk: this.deliveryMaterial?.vermerk },
        dismiss: {
          backdrop: true,
          button: true,
          escape: false,
        },
      }
    );

    if (vermerkResult === 'DISMISSED' || vermerkResult === null) return;

    const bewe = { ...this.deliveryMaterial, vermerk: vermerkResult };
    await this.store.dispatch(
      SOFTLINE_FEATURE_DELIVERY_NOTE,
      DeliveryNoteStore.actions.preparePatchBewe,
      {
        id: this.idKopfLf,
        bewe,
      }
    );
  }

  patchBewe(id: number | string | undefined, seriennummer: BeweLf['seriennr']): void {
    const note = this.store.get(
      SOFTLINE_FEATURE_DELIVERY_NOTE,
      DeliveryNotesStore.getters.collection.entity,
      this.idKopfLf
    );

    const updatedBeweLfs = [...(note.bewelfs ?? [])];
    const index = updatedBeweLfs.findIndex((o) => o.id === id);

    if (index < 0) return;

    const beweLf = updatedBeweLfs[index];

    updatedBeweLfs.splice(index, 1, {
      ...beweLf,
      seriennr: seriennummer,
    });

    this.store.commit(
      SOFTLINE_FEATURE_DELIVERY_NOTE,
      DeliveryNotesStore.mutations.collection.prepare,
      {
        id: this.idKopfLf,
        action: 'patch',
        changes: {
          bewelfs: updatedBeweLfs,
        },
      }
    );
  }

  protected readonly isDefinedNotEmpty = isDefinedNotEmpty;
}
